import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useUserGroups } from '../../hooks/useUserGroups';
import { useResultsGroupsChallenges } from '../../hooks/useResultsGroupsChallenges';
import { useResults } from '../../hooks/useResults';
import { useDeleteResultsChallenge } from '../../hooks/useDeleteResultsChallenge';
import useAppStore from '../../store/appStore';
import ChallengeListResultsClass from './ChallengeListResultsClass';
import LoadingSpinner from '../../common/components/LoadingSpinner/LoadingSpinner';
import { RolesTypes } from "../../constants/roles";

const ChallengeListResults = (props) => {
  const { data: userGroupsData, isLoading: userGroupsLoading, error: userGroupsError } = useUserGroups();
  const { data: challengesData, isLoading: challengesLoading, error: challengesError } = useResultsGroupsChallenges();
  
  // Get user data from Zustand store instead of GraphQL MY_DATA
  const user = useAppStore((state) => state.user);
  const userLoading = !user || !user.me;
  const userData = user ? user.me : null;

  // Calcola courseGroups una volta sola con useMemo
  const courseGroups = useMemo(() => {
    if (!userGroupsData?.userGroups?.records || !userData) return [];
    
    const UserGroupList = [];
    const userGroups = userGroupsData.userGroups.records;
    
    // Calculate courseGroups based on user role
    if (userData.userRole === RolesTypes.SUPERUSER) {
      userGroups.forEach(item => UserGroupList.push(item._id));
    } else {
      // For coaches, filter based on coach groups (simplified for now)
      userGroups.forEach(item => UserGroupList.push(item._id));
    }
    
    return UserGroupList;
  }, [userGroupsData, userData]);

  // Calcola il valore iniziale di challenge basandosi su challengesData
  const initialChallenge = useMemo(() => {
    const challenges = challengesData?.challengeDefinitionNames;
    if (!challenges || challenges.length === 0) {
      return 0;
    }
    return challenges[0].id;
  }, [challengesData]);

  // State for results query parameters
  const [resultsParams, setResultsParams] = useState({
    page: props.match?.params?.page ? parseInt(props.match.params.page) : 0,
    userGroup: null,
    challenge: 0,
    status: 'all'
  });

  // Aggiorna challenge quando challengesData viene caricato
  useEffect(() => {
    if (challengesData && initialChallenge !== resultsParams.challenge) {
      setResultsParams(prev => ({
        ...prev,
        challenge: initialChallenge
      }));
    }
  }, [initialChallenge, challengesData]);

  // Counter to force table re-render after refetch
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Function to update results parameters
  const updateResultsParams = useCallback((newParams) => {
    setResultsParams(prev => ({
      ...prev,
      ...newParams
    }));
  }, []);

  // Query results with current parameters (only when courseGroups and challengesData are loaded)
  const { data: resultsData, isFetching: resultsLoading, error: resultsError, refetch: refetchResults } = useResults({
    page: resultsParams.page,
    courseGroups: courseGroups,
    userGroup: resultsParams.userGroup,
    challenge: resultsParams.challenge,
    status: resultsParams.status,
    enabled: courseGroups.length > 0 && !!challengesData // Enable only when courseGroups and challengesData are loaded
  });

  // Wrapper function to refetch and increment counter
  const handleRefetchResults = useCallback(async () => {
    await refetchResults();
    setRefetchCounter(prev => prev + 1);
  }, [refetchResults]);

  // Delete results challenge mutation
  const deleteResultsChallengeMutation = useDeleteResultsChallenge();

  if (userGroupsLoading || challengesLoading || userLoading) return <LoadingSpinner styles={{ paddingTop: 60 }} />;
  if (userGroupsError) return `Error loading user groups! ${userGroupsError.message}`;
  if (challengesError) return `Error loading challenges! ${challengesError.message}`;
  if (!userData) return `Error loading user data!`;

  // Pass all data and control functions to the class component
  const userGroups = userGroupsData?.userGroups?.records || [];
  const challenges = challengesData?.challengeDefinitionNames || [];
  return (
    <ChallengeListResultsClass 
      {...props} 
      userGroups={userGroups}
      userData={userData}
      challenges={challenges}
      resultsData={resultsData}
      resultsLoading={resultsLoading}
      resultsError={resultsError}
      refetchResults={handleRefetchResults}
      updateResultsParams={updateResultsParams}
      resultsParams={resultsParams}
      deleteResultsChallengeMutation={deleteResultsChallengeMutation}
      refetchCounter={refetchCounter}
      setChallengeOrigineRoute={props.setChallengeOrigineRoute}
    />
  );
};

export default ChallengeListResults;